import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/brand/PageShell";
import { Video, Archive, PlayCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

function fmtLocal(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit", timeZoneName: "short",
        });
    } catch {
        return iso;
    }
}

function useCountdown(iso) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    if (!iso) return { text: "—", secondsUntil: null };
    const target = new Date(iso).getTime();
    const secs = Math.floor((target - now.getTime()) / 1000);
    if (secs <= 0) return { text: "In session", secondsUntil: secs };
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const text = d > 0
        ? `${d}d ${h}h ${m}m`
        : h > 0
            ? `${h}h ${m}m ${String(s).padStart(2, "0")}s`
            : `${m}m ${String(s).padStart(2, "0")}s`;
    return { text, secondsUntil: secs };
}

export default function LiveSessionsPage() {
    const { user } = useAuth();
    const [upcoming, setUpcoming] = useState(null);
    const [inWindow, setInWindow] = useState(false);
    const [archive, setArchive] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    async function load() {
        setLoading(true); setErr(null);
        try {
            const [up, arch] = await Promise.all([api.liveSessionsUpcoming(), api.liveSessionsArchive()]);
            setUpcoming(up.session);
            setInWindow(!!up.in_join_window);
            setArchive(arch.sessions || []);
        } catch (e) { setErr(e.message || "Failed to load live sessions."); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const cd = useCountdown(upcoming?.scheduled_at);

    // Poll upcoming.embed_url visibility as we approach the join window (every 60s).
    useEffect(() => {
        if (!upcoming || inWindow) return;
        const t = setInterval(() => load(), 60_000);
        return () => clearInterval(t);
    }, [upcoming?.id, inWindow]);

    return (
        <PageShell>
            <section className="max-w-6xl mx-auto px-6 py-10" data-testid="live-sessions-page">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Members</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Live Sessions</h1>
                    <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                        Live sessions with the instructor. Bring one question and one setup you want reviewed.
                    </p>
                </div>

                {loading && <div className="mt-8 text-sm" data-testid="live-sessions-loading">Loading live sessions…</div>}
                {err && <div className="mt-8 st-surface p-4 text-sm" style={{ color: "#B42318" }} data-testid="live-sessions-err">{err}</div>}

                {!loading && (
                    <>
                        {/* Upcoming card */}
                        <div className="mt-8 st-surface p-6" data-testid="live-upcoming-card">
                            <div className="flex items-center gap-3">
                                <Video size={20} color="var(--st-royal)" />
                                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Next live session</div>
                            </div>
                            {!upcoming ? (
                                <div className="mt-4 text-sm" style={{ color: "var(--st-ink-muted)" }} data-testid="live-no-upcoming">
                                    No sessions scheduled yet. The instructor will announce the next one shortly.
                                </div>
                            ) : (
                                <>
                                    <h2 className="mt-3 font-serif text-3xl" style={{ color: "var(--st-navy)" }} data-testid="live-upcoming-title">
                                        {upcoming.title}
                                    </h2>
                                    <div className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                                        <span data-testid="live-upcoming-when">{fmtLocal(upcoming.scheduled_at)}</span>
                                        {" · "}
                                        <span>{upcoming.duration_minutes} min</span>
                                        {upcoming.visibility === "phase_3_only" && (
                                            <span style={{ marginLeft: 8, color: "var(--st-royal)" }}>Phase 3 only</span>
                                        )}
                                    </div>
                                    <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded"
                                         style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}>
                                        <Clock size={16} color="var(--st-royal)" />
                                        <span className="font-mono text-sm" style={{ color: "var(--st-navy)" }} data-testid="live-countdown">
                                            {cd.text}
                                        </span>
                                    </div>

                                    {inWindow && upcoming.embed_url ? (
                                        <div className="mt-6">
                                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-royal)" }}>Live now</div>
                                            <div className="mt-2" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                                                <iframe
                                                    src={upcoming.embed_url}
                                                    title={upcoming.title}
                                                    allow="autoplay; fullscreen; picture-in-picture"
                                                    allowFullScreen
                                                    data-testid="live-embed-iframe"
                                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "1px solid var(--st-line)", borderRadius: 6 }}
                                                />
                                            </div>
                                            <a href={upcoming.embed_url} target="_blank" rel="noreferrer"
                                               className="btn-royal mt-4 inline-block" data-testid="live-join-btn">
                                                Open in new tab
                                            </a>
                                        </div>
                                    ) : (
                                        <div className="mt-6 text-xs" style={{ color: "var(--st-silver-500)" }} data-testid="live-not-open-yet">
                                            The join link opens 30 minutes before the scheduled start.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Archive */}
                        <div className="mt-10 st-surface p-6" data-testid="live-archive-card">
                            <div className="flex items-center gap-3">
                                <Archive size={18} color="var(--st-royal)" />
                                <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>Archive</h2>
                            </div>
                            {archive.length === 0 ? (
                                <div className="mt-4 text-sm" style={{ color: "var(--st-ink-muted)" }} data-testid="live-archive-empty">
                                    No recorded sessions yet.
                                </div>
                            ) : (
                                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                                    {archive.map((s) => (
                                        <li key={s.id} className="p-4 rounded"
                                            style={{ background: "var(--st-white)", border: "1px solid var(--st-line)" }}
                                            data-testid={`live-archive-row-${s.id}`}>
                                            <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>{fmtLocal(s.scheduled_at)}</div>
                                            <div className="mt-1 font-serif text-lg" style={{ color: "var(--st-navy)" }}>{s.title}</div>
                                            <a href={s.archive_url} target="_blank" rel="noreferrer"
                                               className="mt-3 btn-outline-navy inline-flex items-center gap-1 text-xs"
                                               data-testid={`live-archive-watch-${s.id}`}>
                                                <PlayCircle size={12} /> Watch recording
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </section>
        </PageShell>
    );
}
